import type { ApiRole } from '@/types/api'

/*
    Also defined in Constants.RoleIds in API layer. That definition is
    used to seed the Database to ensure that the system roles below are
    always defined with these ids.
 */
export const ADMIN_ROLE_ID = -1;
export const SUPER_ADMIN_ROLE_ID = -10;

/**
 * Read-only telemetry role. Grants the admin shell, the dashboard and the analytics page only.
 * It is not an administrative role: no content, publishing or identity privileges follow from it.
 * Server side this is enforced by [AllowSystemRoles(RoleIds.Analytics)] on the two opted-in
 * controllers plus GET /api/admin/languages; everything here is presentation only.
 */
export const ANALYTICS_ROLE_ID = -20;

type Roles = readonly ApiRole[] | undefined | null;

const has = (roles: Roles, id: number) => roles?.some(r => r.id === id) ?? false;

/** Full administrative privileges. Mirrors the server's RoleIds.Admin check. */
export const isFullAdmin = (roles: Roles) => has(roles, ADMIN_ROLE_ID);

export const isSuperAdmin = (roles: Roles) => has(roles, SUPER_ADMIN_ROLE_ID);

/** Holds the analytics role and nothing that would grant broader access. */
export const isAnalyticsOnly = (roles: Roles) => !isFullAdmin(roles) && has(roles, ANALYTICS_ROLE_ID);

/** May open the admin panel at all, whether as a full admin or as a telemetry viewer. */
export const hasAdminPanelAccess = (roles: Roles) => isFullAdmin(roles) || has(roles, ANALYTICS_ROLE_ID);

/**
 * Role names that may open the admin panel, as matched by AuthGuard (case-insensitive, ANY).
 * Server side these come from nameof(RoleIds.X), so they track the seeded Role.Name values.
 */
export const ADMIN_PANEL_ROLE_NAMES = ['Admin', 'Analytics'];
